import { API_BASE_URL } from "@constants/urls";
import authAxios, { apiAxios } from "@helpers/lib/axios";
import { API_ENDPOINTS } from "@memorang/constants";
import type {
	Bundle,
	BundleMetaData,
	MixedItemDistResponse,
	MixedItemTag,
} from "@memorang/types";
import type { MixedItemDistributionRequest } from "./types";

// The live endpoint returns numeric tag/content ids, but packages/types models
// them as string (the app keys sessions/progress on string ids). Coerce at the
// boundary. items[].id stays numeric (Item.id is used numerically downstream).
const coerceTagIds = (tag: MixedItemTag): MixedItemTag => ({
	...tag,
	id: String(tag.id),
	children: tag.children?.map(coerceTagIds),
	childTags: tag.childTags?.map(coerceTagIds),
});

export async function getMixedItemDistribution(
	request: MixedItemDistributionRequest,
): Promise<MixedItemDistResponse> {
	const params: Record<string, string | number> = {
		examId: request.examId,
		action: request.action,
	};
	if (request.collectionId) {
		params.collectionId = request.collectionId;
	}

	const { data } = await apiAxios.get(
		API_ENDPOINTS.ITEM_DISTRIBUTION(request.userPublicMetadataId),
		{ params },
	);

	const raw = data.data as MixedItemDistResponse;
	return {
		...raw,
		// The roster/identity now comes from `exams`; `tags` feeds only mastery, so
		// drop the pre/post-test aggregate tags (type "Practice Test", e.g. "Pre Test
		// MCQs") that would otherwise surface as bogus mastery cards.
		tags: (raw.tags ?? [])
			.filter((tag) => tag.type !== "Practice Test")
			.map(coerceTagIds),
		contents: (raw.contents ?? []).map((content) => ({
			...content,
			id: String(content.id),
		})),
		freeTagIds: (raw.freeTagIds ?? []).map(String),
	};
}

export async function fetchProductDetails({
	tenantId,
	productId,
}: {
	tenantId: string;
	productId: number;
}): Promise<Bundle> {
	const { data } = await authAxios.post(
		`${API_BASE_URL}/aibl-query/get-product-details`,
		{
			numericId: productId,
			tenantId,
		},
	);

	return data.data.productDetails;
}

export async function fetchProductMetadata({
	tenantId,
	productId,
	userId,
}: {
	tenantId: string;
	productId: string;
	userId: string;
}): Promise<BundleMetaData> {
	const { data } = await authAxios.post(
		`${API_BASE_URL}/aibl-query/get-product-metadata`,
		{
			productId,
			tenantId,
			userId,
		},
	);

	return data.data;
}
